import React from "react";
import PdfLayout from '../PdfLayout';
import PdfLayoutWidget from './PdfLayoutWidget';
import { Screen } from '../../services';
import {
    buildProgrammePreviewUnits,
    formatProgrammePreviewTime,
} from '../../services/pdfLayoutPreviewSample';

/**
 * Catch-all content widget for screenings-programme PDFs.
 *
 * The reserved box is filled by the engine with day headers + screening rows;
 * when it overflows, the engine clones the continuation page template.
 */
class ProgrammeBodyWidget extends PdfLayoutWidget {

    constructor(properties) {
        super({
            ...(ProgrammeBodyWidget.getDefaultOptions()),
            ...properties,
        });

        this.type    = PdfLayout.TEXT;
        this.subType = PdfLayout.PROGRAMME_BODY;
    }

    static getDefaultProgrammeOptions() {
        return {
            show_day_header: true,
            day_date_format: 'dddd D MMMM',
            day_style: 'text',
            day_background: '#000000',
            day_color: '#FFFFFF',
            day_border_radius: 8,
            day_font_size: 14,
            day_font_family: 'Helvetica-Bold',
            day_padding_v_cm: 0.22,
            day_margin_bottom_cm: 0.3,
            columns: {
                time: true,
                time_range: false,
                title: true,
                version: true,
                place: false,
            },
            time_style: 'text',
            time_format: 'HH:mm',
            time_range_separator: ' > ',
            time_background: '#000000',
            time_color: '#FFFFFF',
            time_border_radius: 6,
            time_font_size: 11,
            time_font_family: 'Helvetica-Bold',
            time_width_cm: 2.2,
            title_transform: 'none',
            title_font_size: 12,
            title_font_family: 'Helvetica-Bold',
            title_color: '#000000',
            version_font_size: 11,
            version_font_family: 'Helvetica',
            version_color: '#000000',
            version_width_cm: 1.6,
            place_font_size: 10,
            place_color: '#333333',
            show_separator: true,
            separator_color: '#000000',
            separator_width: 0.5,
            row_padding_v_cm: 0.18,
            row_gap_cm: 0.05,
            repeat_day_header_on_overflow: true,
        };
    }

    static getDefaultOptions() {
        return {
            position: { x: 1.5, y: 4, z: 0 },
            dimensions: { width: 18, height: 18 },
            transform: { rotation: 0 },
            options: {
                programme: ProgrammeBodyWidget.getDefaultProgrammeOptions(),
                style: {
                    color: '#000000',
                    fontFamily: 'Helvetica',
                    fontSize: 10,
                    lineHeight: 1.2,
                    textAlign: 'left',
                    backgroundColor: '#FFFFFF00',
                    borderStyle: 'solid',
                    borderWidth: 0,
                    borderColor: '#000000',
                    borderRadius: 0,
                    padding: {
                        top: 0.1,
                        right: 0.1,
                        bottom: 0.1,
                        left: 0.1,
                    },
                },
            },
        };
    }

    getHandledOptions() {
        return [
            PdfLayout.OPTION_X,
            PdfLayout.OPTION_Y,
            PdfLayout.OPTION_Z,
            PdfLayout.OPTION_WIDTH,
            PdfLayout.OPTION_HEIGHT,
            PdfLayout.OPTION_COLOR,
            PdfLayout.OPTION_FONT_FAMILY,
            PdfLayout.OPTION_FONT_SIZE,
            PdfLayout.OPTION_LINE_HEIGHT,
            PdfLayout.OPTION_TEXT_ALIGNMENT,
            PdfLayout.OPTION_PADDING,
            PdfLayout.OPTION_BACKGROUND_COLOR,
            PdfLayout.OPTION_BORDER_COLOR,
            PdfLayout.OPTION_BORDER_STYLE,
            PdfLayout.OPTION_BORDER_WIDTH,
            PdfLayout.OPTION_BORDER_RADIUS,
            PdfLayout.OPTION_PROGRAMME,
        ];
    }

    getIcon() {
        return 'calendar';
    }

    applyTitleTransform(title, transform) {
        const value = String(title || '');
        switch (transform) {
            case 'uppercase':
                return value.toUpperCase();
            case 'lowercase':
                return value.toLowerCase();
            case 'capitalize':
                return value.replace(/(^\w|[^\w']\w)/g, (ch) => ch.toUpperCase());
            default:
                return value;
        }
    }

    resolveDayTextColor(programme) {
        const bg = String(programme.day_background || '#000000').toLowerCase();
        let fg = String(programme.day_color || '#FFFFFF').toLowerCase();
        if (programme.day_style === 'pill') {
            const darkFg = fg === '#000' || fg === '#000000';
            const darkBg = bg === '#000' || bg === '#000000' || bg === '#111' || bg === '#111111';
            if (darkFg && darkBg)
                return '#FFFFFF';
            return programme.day_color || '#FFFFFF';
        }
        if (fg === '#fff' || fg === '#ffffff')
            return '#000000';
        return programme.day_color || '#000000';
    }

    getPreview(props) {
        const { lang } = props;
        const programme = {
            ...ProgrammeBodyWidget.getDefaultProgrammeOptions(),
            ...(this.options?.programme || {}),
        };
        const units = buildProgrammePreviewUnits(
            lang,
            programme.day_date_format || 'dddd D MMMM'
        );
        const dayFg = this.resolveDayTextColor(programme);
        const dayBg = programme.day_background || '#000000';
        const timePill = programme.time_style === 'pill';
        const dayPill = programme.day_style === 'pill';
        const showVersion = programme.columns?.version !== false;
        const showPlace = programme.columns?.place === true;
        const titleFs = Screen.ppToPx(programme.title_font_size || 12);
        const timeFs = Screen.ppToPx(programme.time_font_size || 11);
        const dayFs = Screen.ppToPx(programme.day_font_size || 14);
        const timeW = Screen.cmToPx(programme.time_width_cm || 2.2);

        return (
            <div style={{
                width: Screen.cmToPx(this.dimensions?.width),
                height: Screen.cmToPx(this.dimensions?.height),
                color: this.options?.style?.color || '#000000',
                fontSize: Screen.ppToPx(this.options?.style?.fontSize || 10),
                lineHeight: this.options?.style?.lineHeight || 1.2,
                textAlign: this.options?.style?.textAlign || 'left',
                backgroundColor: this.options?.style?.backgroundColor || 'transparent',
                border: `${this.options?.style?.borderWidth || 0}px ${this.options?.style?.borderStyle || 'solid'} ${this.options?.style?.borderColor || '#000'}`,
                borderRadius: `${this.options?.style?.borderRadius || 0}px`,
                paddingTop: `${Screen.cmToPx(this.options?.style?.padding?.top || 0)}px`,
                paddingRight: `${Screen.cmToPx(this.options?.style?.padding?.right || 0)}px`,
                paddingBottom: `${Screen.cmToPx(this.options?.style?.padding?.bottom || 0)}px`,
                paddingLeft: `${Screen.cmToPx(this.options?.style?.padding?.left || 0)}px`,
                overflow: 'hidden',
                boxSizing: 'border-box',
            }}>
                {units.map((unit, index) => {
                    if (unit.type === 'day') {
                        if (programme.show_day_header === false)
                            return null;
                        return (
                            <div
                                key={`day-${unit.day_key}-${index}`}
                                style={{
                                    backgroundColor: dayPill ? dayBg : 'transparent',
                                    color: dayFg,
                                    fontWeight: 'bold',
                                    fontSize: dayFs,
                                    borderRadius: dayPill ? (programme.day_border_radius || 8) : 0,
                                    padding: `${Screen.cmToPx(programme.day_padding_v_cm || 0.22)}px ${Screen.cmToPx(0.15)}px`,
                                    marginBottom: Screen.cmToPx(programme.day_margin_bottom_cm || 0.3),
                                    textTransform: 'uppercase',
                                }}
                            >
                                {unit.label}
                            </div>
                        );
                    }

                    return (
                        <div key={`row-${unit.day_key}-${index}`}>
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: Screen.cmToPx(0.2),
                                paddingTop: Screen.cmToPx(programme.row_padding_v_cm || 0.18),
                                paddingBottom: Screen.cmToPx(programme.row_padding_v_cm || 0.18),
                            }}>
                                {programme.columns?.time !== false && (
                                    <div style={{
                                        width: timeW,
                                        flex: '0 0 auto',
                                        backgroundColor: timePill ? (programme.time_background || '#000') : 'transparent',
                                        color: timePill ? (programme.time_color || '#FFF') : '#000',
                                        fontWeight: 'bold',
                                        fontSize: timeFs,
                                        borderRadius: timePill ? (programme.time_border_radius || 6) : 0,
                                        textAlign: timePill ? 'center' : 'left',
                                        padding: timePill ? '2px 0' : 0,
                                    }}>
                                        {formatProgrammePreviewTime(unit, programme)}
                                    </div>
                                )}
                                <div style={{
                                    flex: '1 1 auto',
                                    fontWeight: 'bold',
                                    fontSize: titleFs,
                                    color: programme.title_color || '#000',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                }}>
                                    {this.applyTitleTransform(unit.title, programme.title_transform)}
                                </div>
                                {showPlace && unit.place && (
                                    <div style={{
                                        flex: '0 0 auto',
                                        fontSize: Screen.ppToPx(programme.place_font_size || 10),
                                        color: programme.place_color || '#333',
                                    }}>
                                        {unit.place}
                                    </div>
                                )}
                                {showVersion && (
                                    <div style={{
                                        width: Screen.cmToPx(programme.version_width_cm || 1.6),
                                        flex: '0 0 auto',
                                        textAlign: 'right',
                                        fontSize: Screen.ppToPx(programme.version_font_size || 11),
                                        color: programme.version_color || '#000',
                                    }}>
                                        {unit.version}
                                    </div>
                                )}
                            </div>
                            {programme.show_separator !== false && (
                                <div style={{
                                    borderBottom: `${programme.separator_width || 0.5}px solid ${programme.separator_color || '#000'}`,
                                    marginBottom: Screen.cmToPx(programme.row_gap_cm || 0.05),
                                }} />
                            )}
                        </div>
                    );
                })}
            </div>
        );
    }
}

export default ProgrammeBodyWidget;
